import type { ChatImage } from "@/core/components/chat-images";

export type AiScriptExplanationChatRole = "user" | "assistant";

export interface IAiScriptExplanationChatMessage {
  role: AiScriptExplanationChatRole;
  content: string;
}

export interface IAiScriptExplanationChatSource {
  id?: string | null;
  type?: string | null;
  title?: string | null;
  label?: string | null;
  detail?: string | null;
  description?: string | null;
  job_id?: string | null;
  invoice_id?: string | null;
  help_topic_id?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface IAiScriptExplanationChatRunContext {
  business_id: string;
  ai_script_run_id: string;
  ai_script_version_id: string | null;
  pay_period_id: string | null;
  status: string;
  pay_period?: {
    start_date: string | null;
    end_date: string | null;
  } | null;
  generated_workbook?: {
    filename?: string | null;
    sheet_count?: number | null;
    cell_count?: number | null;
    content_type?: string | null;
  } | null;
}

export const AI_SCRIPT_CHAT_CONTEXT_KIND = {
  cell: "cell",
  row: "row",
  column: "column",
} as const;

export type AiScriptChatContextKind =
  (typeof AI_SCRIPT_CHAT_CONTEXT_KIND)[keyof typeof AI_SCRIPT_CHAT_CONTEXT_KIND];

export interface IAiScriptChatCellContext {
  kind: typeof AI_SCRIPT_CHAT_CONTEXT_KIND.cell;
  sheetName: string;
  coordinate: string;
  value: string | null;
  columnHeader: string | null;
  rowLabel: string | null;
}

export interface IAiScriptChatRowContext {
  kind: typeof AI_SCRIPT_CHAT_CONTEXT_KIND.row;
  sheetName: string;
  rowNumber: number;
  rowLabel: string | null;
  cells: { header: string | null; value: string }[];
}

export interface IAiScriptChatColumnContext {
  kind: typeof AI_SCRIPT_CHAT_CONTEXT_KIND.column;
  sheetName: string;
  columnLetter: string;
  columnHeader: string | null;
  values: { rowLabel: string | null; value: string }[];
  isTruncated: boolean;
}

export type AiScriptChatSelectionContext =
  | IAiScriptChatCellContext
  | IAiScriptChatRowContext
  | IAiScriptChatColumnContext;

export interface IAiScriptExplanationChatError {
  code: string;
  message: string;
}

export interface IAiScriptExplanationChatResponse {
  message: string;
  sources: IAiScriptExplanationChatSource[];
  run_context: IAiScriptExplanationChatRunContext | null;
  error: IAiScriptExplanationChatError | null;
}

export interface IAiScriptExplanationChatThread {
  id: string;
  name: string;
  purpose: string;
  status: string;
  subject_type: string;
  subject_id: string;
  created_by_id: string | null;
  last_message_at: string | null;
  created_at: string;
  updated_at: string;
  running: boolean;
  active_turn: IAiScriptExplanationChatActiveTurn | null;
}

export interface IAiScriptExplanationChatActiveTurn {
  turn_id: string;
  status: string;
  started_at: string | null;
  updated_at: string | null;
}

export const AI_CHAT_MESSAGE_ROLE = {
  user: "user",
  assistant: "assistant",
  error: "error",
} as const;

export type AiChatMessageRole =
  (typeof AI_CHAT_MESSAGE_ROLE)[keyof typeof AI_CHAT_MESSAGE_ROLE];

export interface IAiScriptExplanationChatPersistedMessage {
  id: string;
  role: AiChatMessageRole;
  content: string;
  sequence: number;
  metadata: {
    supported_help_topic_ids?: string[];
    sources?: IAiScriptExplanationChatSource[] | null;
    run_context?: IAiScriptExplanationChatRunContext | null;
    error?: IAiScriptExplanationChatError | null;
  } | null;
  created_by_id: string | null;
  created_at: string;
  /** Screenshots sent with it, on the chats that take them. */
  images?: ChatImage[];
}

export interface IAiScriptExplanationChatTurnResponse {
  turn_id: string;
  thread: IAiScriptExplanationChatThread;
}

export interface IAiScriptExplanationChatThreadsResponse {
  threads: IAiScriptExplanationChatThread[];
}

export interface IAiScriptExplanationChatThreadResponse {
  thread: IAiScriptExplanationChatThread;
  messages: IAiScriptExplanationChatPersistedMessage[];
}

export interface IAiScriptExplanationChatStreamEvent {
  kind:
    | "event_started"
    | "progress"
    | "text_delta"
    | "event_finished"
    | "error";
  content?: string | null;
  seq: number;
}

export interface IAiScriptExplanationChatStreamResponse {
  status: string;
  events: IAiScriptExplanationChatStreamEvent[];
  cursor: number;
}
